import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useToast } from '../../contexts/ToastContext';
import {
  retailerCatalogApi,
  type DiscoveryResult,
  type CataloguePreview,
  type CatalogueSearchItem,
} from '../../services/retailerCatalog';
import type { Catalogue, CatalogueScopeRules } from '@traaker/shared';
import { WafWhitelistInstructions } from './WafWhitelistInstructions';
import { CatalogueIcon, CATALOGUE_ICON_KEYS } from './CatalogueIcon';

/** The include glob a checked row contributes: a bounded path segment. */
function itemGlob(categoryPath: string): string {
  return `**/${categoryPath}/**`;
}
const SEARCH_RESULT_LIMIT = 50;

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

const CSV_MAX_BYTES = 8 * 1024 * 1024;

type Step = 'source' | 'preview';
type Source = 'sitemap' | 'upload';

/**
 * Parse-first create flow (Phase 1, reworked):
 *   source step  → detect sitemap (pick which files) OR upload a CSV file
 *   preview step → metadata of what was parsed + scope with LIVE recount → create
 * Slug is derived server-side; language is a flag chip.
 */
export function CatalogueCreateDialog({
  open,
  brandId,
  defaultDomain,
  defaultLanguage,
  onClose,
  onCreated,
}: {
  open: boolean;
  brandId: string;
  defaultDomain: string;
  defaultLanguage: string;
  onClose: () => void;
  onCreated: (c: Catalogue) => void;
}) {
  const { show } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('source');
  const [source, setSource] = useState<Source>('sitemap');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string>('store');
  const [domain, setDomain] = useState(defaultDomain);
  const [language, setLanguage] = useState(
    LANGUAGES.some((l) => l.code === defaultLanguage) ? defaultLanguage : 'fr',
  );

  // sitemap discovery
  const [discovery, setDiscovery] = useState<DiscoveryResult | null>(null);
  const [selectedSitemaps, setSelectedSitemaps] = useState<string[]>([]);
  const [discovering, setDiscovering] = useState(false);

  // csv
  const [csv, setCsv] = useState('');
  const [csvName, setCsvName] = useState('');

  // preview + scope
  const [preview, setPreview] = useState<CataloguePreview | null>(null);
  const [parsing, setParsing] = useState(false);
  // Set when the user overrode the auto-detected product/category split (sitemaps
  // whose product URLs carry no recognizable id — nothing in the URL to detect).
  const [forcedItemType, setForcedItemType] = useState<'product' | undefined>();
  const [includeText, setIncludeText] = useState('');
  const [excludeText, setExcludeText] = useState('');
  // categoryPaths picked via search/category checkboxes (kept out of includeText).
  const [pickedPaths, setPickedPaths] = useState<string[]>([]);
  const [recount, setRecount] = useState<{ matched: number; matchedProducts: number } | null>(null);
  const [recounting, setRecounting] = useState(false);
  const [creating, setCreating] = useState(false);

  // "how to unblock" popup (shown when discovery is WAF-blocked)
  const [wafHelpOpen, setWafHelpOpen] = useState(false);

  // scope keyword search (server-side over the cached preview candidate set)
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<{ rows: CatalogueSearchItem[]; total: number }>({
    rows: [],
    total: 0,
  });

  useEffect(() => {
    if (open) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Include scope has TWO sources kept separate so the manual textarea stays short:
  //   - pickedPaths: categoryPaths chosen via search/category checkboxes → globs,
  //     shown ONLY in the collapsible "sélectionnés via la recherche" strip.
  //   - includeText: manually-typed glob patterns, shown in the textarea.
  // scopeRules() merges both (deduped).
  const manualIncludes = () => includeText.split('\n').map((s) => s.trim()).filter(Boolean);
  const scopeRules = (): CatalogueScopeRules => ({
    include: [...new Set([...pickedPaths.map(itemGlob), ...manualIncludes()])],
    exclude: excludeText.split('\n').map((s) => s.trim()).filter(Boolean),
  });
  const hasScope =
    pickedPaths.length > 0 || includeText.trim() !== '' || excludeText.trim() !== '';

  // ---- scope keyword search (server-side over the cached candidate set) ----
  // The full 50k set (~6.4MB) exceeds the 6MB Lambda response cap, so we can't ship
  // it to filter in-browser — instead debounce a search call that returns top matches.
  useEffect(() => {
    const q = query.trim();
    if (step !== 'preview' || !preview || !q) {
      setResults({ rows: [], total: 0 });
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const r = await retailerCatalogApi.searchPreview(brandId, {
          previewId: preview.previewId,
          q,
          limit: SEARCH_RESULT_LIMIT,
        });
        setResults(r);
      } catch {
        setResults({ rows: [], total: 0 });
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, step, preview, brandId]);

  // Search/category picks are their own path list (NOT in the textarea).
  const selectedPaths = pickedPaths;
  const isSelected = (categoryPath: string) => pickedPaths.includes(categoryPath);
  const toggleItem = (categoryPath: string) =>
    setPickedPaths((prev) =>
      prev.includes(categoryPath)
        ? prev.filter((p) => p !== categoryPath)
        : [...prev, categoryPath],
    );
  // "Tout ajouter" hands back globs; store their inner paths as picks (dedup).
  const addGlobs = (globs: string[]) =>
    setPickedPaths((prev) => {
      const set = new Set(prev);
      for (const g of globs) {
        const p = g.match(/^\*\*\/(.+)\/\*\*$/)?.[1];
        if (p) set.add(p);
      }
      return [...set];
    });

  // Collapsed by default — the selection list gets very long with many filters.
  const [showSelected, setShowSelected] = useState(false);

  // "Tout ajouter" — add EVERY match of the current query (not just the visible 50).
  const [bulkAdding, setBulkAdding] = useState(false);
  const BULK_CONFIRM_THRESHOLD = 1000;
  const doBulkAdd = async () => {
    if (!preview || !query.trim()) return;
    if (
      results.total > BULK_CONFIRM_THRESHOLD &&
      !window.confirm(
        `Ajouter ${results.total.toLocaleString()} éléments au périmètre ? La liste d'inclusion sera longue mais fonctionnera.`,
      )
    )
      return;
    setBulkAdding(true);
    try {
      const r = await retailerCatalogApi.matchGlobs(brandId, {
        previewId: preview.previewId,
        q: query.trim(),
      });
      addGlobs(r.globs);
      show(
        r.capped
          ? `Plafond atteint : ${r.globs.length.toLocaleString()} motifs ajoutés (sur ${r.total.toLocaleString()} résultats) — affinez la recherche pour le reste.`
          : `${r.total.toLocaleString()} éléments ajoutés au périmètre.`,
        { severity: r.capped ? 'warning' : 'success' },
      );
    } catch (e: any) {
      show(e?.message || 'Ajout impossible', { severity: 'error' });
    } finally {
      setBulkAdding(false);
    }
  };

  // ---- source step actions ----
  const doDiscover = async () => {
    if (!domain.trim()) return;
    setDiscovering(true);
    setDiscovery(null);
    setSelectedSitemaps([]);
    try {
      const d = await retailerCatalogApi.discover(brandId, domain);
      setDiscovery(d);
      setSelectedSitemaps(d.sitemapUrls); // all selected by default
      if (!d.sitemapUrls.length) {
        setSource('upload');
        show(
          d.wafBlocked
            ? 'Sitemap bloqué par un pare-feu — importez un CSV ou autorisez notre robot.'
            : 'Aucun sitemap détecté — importez un CSV.',
          {
            severity: 'warning',
            action: d.wafBlocked
              ? { label: 'Débloquer', onClick: () => setWafHelpOpen(true) }
              : undefined,
          },
        );
      }
    } catch (e: any) {
      show(e?.message || 'Détection impossible', { severity: 'error' });
    } finally {
      setDiscovering(false);
    }
  };

  // A ready-to-fill CSV template: the recognized header + example rows.
  // Only `url` is required; type (produit / catégorie) is auto-détecté depuis
  // l'URL. `category` est un chemin optionnel séparé par des `/`.
  const downloadTemplate = () => {
    const csv =
      'url,name,image,category\n' +
      // Une page produit
      'https://exemple.com/produits/plancha-electrique-2000w,' +
      'Plancha électrique 2000 W,' +
      'https://exemple.com/img/plancha-2000w.jpg,' +
      'Jardin/Cuisine extérieure/Planchas\n' +
      // Une page catégorie (liste de produits) — name/image/category facultatifs
      'https://exemple.com/jardin/cuisine-exterieure,,,\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele-catalogue.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPickFile = (file?: File) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      show('Sélectionnez un fichier .csv', { severity: 'warning' });
      return;
    }
    if (file.size > CSV_MAX_BYTES) {
      show('Fichier trop volumineux (max 8 Mo).', { severity: 'warning' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCsv(String(reader.result || ''));
      setCsvName(file.name);
    };
    reader.onerror = () => show('Lecture du fichier impossible.', { severity: 'error' });
    reader.readAsText(file);
  };

  /** Parse the selected source. `itemType` re-parses the SAME sitemaps with the
   *  product/category verdict forced (the "ce sont des produits" override). */
  const doPreview = async (itemType?: 'product') => {
    setParsing(true);
    try {
      const body =
        source === 'sitemap'
          ? { source: 'sitemap' as const, sitemapUrls: selectedSitemaps, itemType }
          : { source: 'upload' as const, csv };
      const p = await retailerCatalogApi.preview(brandId, body);
      // A re-parse rebuilds every categoryPath, so a scope built on the previous
      // parse no longer refers to anything — drop it rather than silently mismatch.
      setPickedPaths([]);
      setIncludeText('');
      setExcludeText('');
      setQuery('');
      setForcedItemType(itemType);
      setPreview(p);
      setRecount({ matched: p.totalItems, matchedProducts: p.totalProducts });
      setStep('preview');
      if (p.truncated) {
        show('Catalogue volumineux — tronqué à 50 000 éléments. Affinez le périmètre.', {
          severity: 'warning',
        });
      }
    } catch (e: any) {
      show(e?.message || 'Analyse impossible', { severity: 'error' });
    } finally {
      setParsing(false);
    }
  };

  // ---- live recount (debounced) ----
  useEffect(() => {
    if (step !== 'preview' || !preview) return;
    if (!hasScope) {
      setRecount({ matched: preview.totalItems, matchedProducts: preview.totalProducts });
      return;
    }
    const handle = setTimeout(async () => {
      setRecounting(true);
      try {
        const r = await retailerCatalogApi.recount(brandId, {
          previewId: preview.previewId,
          scopeRules: scopeRules(),
        });
        setRecount({ matched: r.matched, matchedProducts: r.matchedProducts });
      } catch {
        /* keep last count */
      } finally {
        setRecounting(false);
      }
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeText, excludeText, pickedPaths, step, preview]);

  const doCreate = async () => {
    if (!name.trim() || !preview) {
      show('Renseignez le nom du catalogue.', { severity: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const res = await retailerCatalogApi.create(brandId, {
        name,
        description: description.trim() || undefined,
        icon,
        domain,
        language,
        previewId: preview.previewId,
        source,
        sitemapUrls: source === 'sitemap' ? selectedSitemaps : undefined,
        // Persisted on the catalogue so refresh re-harvests with the same declaration.
        itemType: source === 'sitemap' ? forcedItemType : undefined,
        wafBlocked: discovery?.wafBlocked,
        scopeRules: hasScope ? scopeRules() : undefined,
      });
      show(res.truncated ? 'Catalogue créé (tronqué à 50 000).' : 'Catalogue créé', {
        severity: res.truncated ? 'warning' : 'success',
      });
      onCreated(res.catalogue);
    } catch (e: any) {
      show(e?.message || 'Création impossible', { severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const resetAll = () => {
    setStep('source');
    setSource('sitemap');
    setName('');
    setDescription('');
    setIcon('store');
    setDomain(defaultDomain);
    setDiscovery(null);
    setSelectedSitemaps([]);
    setCsv('');
    setCsvName('');
    setPreview(null);
    setForcedItemType(undefined);
    setIncludeText('');
    setExcludeText('');
    setPickedPaths([]);
    setShowSelected(false);
    setRecount(null);
    setQuery('');
  };

  const canPreview =
    source === 'sitemap' ? selectedSitemaps.length > 0 : csv.trim().length > 0;

  const shortName = (u: string) => {
    try {
      return new URL(u).pathname.split('/').filter(Boolean).pop() || u;
    } catch {
      return u;
    }
  };

  return (
    <>
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Nouveau catalogue{step === 'preview' ? ' — périmètre' : ''}
      </DialogTitle>
      <DialogContent>
        {step === 'source' ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Domaine du site"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              size="small"
              fullWidth
              placeholder="www.traaker.ai"
            />

            <ToggleButtonGroup
              value={source}
              exclusive
              size="small"
              onChange={(_, v) => v && setSource(v)}
            >
              <ToggleButton value="sitemap">Détection automatique</ToggleButton>
              <ToggleButton value="upload">Import CSV</ToggleButton>
            </ToggleButtonGroup>

            {source === 'sitemap' && (
              <>
                <Button variant="outlined" onClick={doDiscover} disabled={discovering || !domain.trim()}>
                  {discovering ? <CircularProgress size={20} /> : 'Détecter les sitemaps'}
                </Button>
                {discovery && discovery.sitemapUrls.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {discovery.sitemapUrls.length} sitemap(s) détecté(s) via{' '}
                      <strong>{discovery.source}</strong>
                      {discovery.wafBlocked && ' (pare-feu contourné)'}. Sélectionnez ceux à importer :
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {selectedSitemaps.length} / {discovery.sitemapUrls.length} sélectionné
                        {selectedSitemaps.length > 1 ? 's' : ''}
                      </Typography>
                      <Box>
                        <Button
                          size="small"
                          onClick={() => setSelectedSitemaps(discovery.sitemapUrls)}
                          disabled={selectedSitemaps.length === discovery.sitemapUrls.length}
                        >
                          Tout sélectionner
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setSelectedSitemaps([])}
                          disabled={selectedSitemaps.length === 0}
                        >
                          Tout désélectionner
                        </Button>
                      </Box>
                    </Stack>
                    <FormGroup
                      sx={{
                        maxHeight: 260,
                        overflow: 'auto',
                        pl: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      {discovery.sitemapUrls.map((u) => (
                        <FormControlLabel
                          key={u}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedSitemaps.includes(u)}
                              onChange={(e) =>
                                setSelectedSitemaps((prev) =>
                                  e.target.checked ? [...prev, u] : prev.filter((x) => x !== u),
                                )
                              }
                            />
                          }
                          label={<Typography variant="caption">{shortName(u)}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}
                {discovery && discovery.sitemapUrls.length === 0 && (
                  <Alert
                    severity="warning"
                    action={
                      discovery.wafBlocked ? (
                        <Button color="inherit" size="small" onClick={() => setWafHelpOpen(true)}>
                          Débloquer
                        </Button>
                      ) : undefined
                    }
                  >
                    Aucun sitemap exploitable{discovery.wafBlocked ? ' (bloqué par un pare-feu)' : ''}.
                    {discovery.wafBlocked
                      ? ' Autorisez notre robot ou importez un CSV.'
                      : " Basculez sur l'import CSV."}
                  </Alert>
                )}
              </>
            )}

            {source === 'upload' && (
              <Box>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileRef.current?.click()}
                  >
                    {csvName || 'Choisir un fichier CSV'}
                  </Button>
                  <Button startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                    Télécharger un modèle
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Fichier .csv uniquement (max 8 Mo). Une ligne = un élément (produit
                  ou catégorie). Seule la colonne <b>url</b> est obligatoire ; le type
                  est détecté automatiquement depuis l'URL. Colonnes facultatives :{' '}
                  <b>name</b>, <b>image</b>, <b>category</b> (chemin séparé par des «&nbsp;/&nbsp;»,
                  ex. <code>Jardin/Cuisine extérieure/Planchas</code>).
                </Typography>
              </Box>
            )}
          </Stack>
        ) : (
          // ---- preview step ----
          <Stack spacing={2} sx={{ mt: 1 }}>
            {preview && (
              <Alert severity="info" icon={false}>
                <Typography variant="body2" fontWeight={600}>
                  {preview.totalItems.toLocaleString()} éléments détectés ·{' '}
                  {preview.totalProducts.toLocaleString()} produits ·{' '}
                  {preview.totalCategories.toLocaleString()} catégories
                  {preview.truncated && ' (tronqué à 150 000)'}
                </Typography>
              </Alert>
            )}

            {/* Some sites carry NO product marker in their URLs (id-less PrestaShop
                friendly URLs, flat WordPress slugs) — nothing to detect, so let the
                user declare it rather than ship a catalogue of fake categories. */}
            {preview && source === 'sitemap' && !forcedItemType && preview.totalProducts === 0 && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled={parsing}
                    onClick={() => doPreview('product')}
                    sx={{ flexShrink: 0 }}
                  >
                    {parsing ? <CircularProgress size={16} /> : 'Ce sont des produits'}
                  </Button>
                }
              >
                Aucun produit reconnu — les URLs de ce site ne contiennent aucun
                identifiant produit. Si ces pages sont bien des fiches produit, forcez le
                type.
              </Alert>
            )}
            {preview && forcedItemType === 'product' && (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled={parsing}
                    onClick={() => doPreview()}
                    sx={{ flexShrink: 0 }}
                  >
                    {parsing ? <CircularProgress size={16} /> : 'Détection auto'}
                  </Button>
                }
              >
                Type forcé : toutes les URLs des sitemaps sélectionnés sont traitées comme
                des produits.
              </Alert>
            )}

            {preview && preview.topCategoryPaths.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Principales sections détectées (cliquez pour restreindre) :
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {preview.topCategoryPaths.slice(0, 12).map((c) => (
                    <Chip
                      key={c.path}
                      size="small"
                      label={`${c.path} (${c.count})`}
                      color={isSelected(c.path) ? 'primary' : 'default'}
                      variant={isSelected(c.path) ? 'filled' : 'outlined'}
                      onClick={() => toggleItem(c.path)}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {preview && preview.totalItems > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Rechercher des produits ou catégories
                </Typography>
                <TextField
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="ex. housse de couette, casio, emmental…"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {selectedPaths.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedPaths.length} élément{selectedPaths.length > 1 ? 's' : ''} sélectionné
                        {selectedPaths.length > 1 ? 's' : ''} via la recherche
                      </Typography>
                      <Button size="small" sx={{ minWidth: 0, py: 0 }} onClick={() => setShowSelected((s) => !s)}>
                        {showSelected ? 'Masquer' : 'Voir'}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        sx={{ minWidth: 0, py: 0 }}
                        onClick={() => setPickedPaths([])}
                      >
                        Tout retirer
                      </Button>
                    </Stack>
                    {showSelected && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.5, maxHeight: 160, overflow: 'auto' }}
                      >
                        {selectedPaths.map((p) => (
                          <Chip
                            key={p}
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={p.length > 40 ? `…${p.slice(-40)}` : p}
                            onDelete={() => toggleItem(p)}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
                {query.trim() !== '' && (
                  <Box sx={{ mt: 1 }}>
                    {searching ? (
                      <LinearProgress />
                    ) : results.total === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Aucun résultat pour « {query.trim()} ».
                      </Typography>
                    ) : (
                      <>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {results.total > SEARCH_RESULT_LIMIT
                              ? `${SEARCH_RESULT_LIMIT} premiers sur ${results.total.toLocaleString()} — affinez la recherche`
                              : `${results.total} résultat${results.total > 1 ? 's' : ''}`}{' '}
                            · cochez pour ajouter au périmètre
                          </Typography>
                          <Button
                            size="small"
                            onClick={doBulkAdd}
                            disabled={bulkAdding}
                            sx={{ flexShrink: 0 }}
                          >
                            {bulkAdding ? (
                              <CircularProgress size={16} />
                            ) : (
                              `Tout ajouter (${results.total.toLocaleString()})`
                            )}
                          </Button>
                        </Stack>
                        <FormGroup
                          sx={{
                            maxHeight: 240,
                            overflow: 'auto',
                            mt: 0.5,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1,
                          }}
                        >
                          {results.rows.map((it, i) => {
                            const sel = isSelected(it.c);
                            return (
                              <FormControlLabel
                                key={`${it.c}#${i}`}
                                sx={{
                                  m: 0,
                                  borderRadius: 1,
                                  bgcolor: sel ? 'action.selected' : 'transparent',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={sel}
                                    onChange={() => toggleItem(it.c)}
                                  />
                                }
                                label={
                                  <Box sx={{ py: 0.25 }}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 460 }}>
                                      {it.n || it.c}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      sx={{ maxWidth: 460, display: 'block' }}
                                    >
                                      {it.t === 'p' ? 'Produit' : 'Catégorie'} · {it.c}
                                    </Typography>
                                  </Box>
                                }
                              />
                            );
                          })}
                        </FormGroup>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Périmètre avancé (optionnel)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Motifs glob supplémentaires, un par ligne (les sélections par recherche
                ci-dessus sont gérées à part). ** = tout · * = hors / · ? = un caractère.
                Exclure l'emporte. Vide = tout.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  label="Inclure"
                  value={includeText}
                  onChange={(e) => setIncludeText(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="**/electromenager/**"
                />
                <TextField
                  label="Exclure"
                  value={excludeText}
                  onChange={(e) => setExcludeText(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="**/promos/**"
                />
              </Stack>
              <Box sx={{ mt: 1, minHeight: 24 }}>
                {recounting ? (
                  <LinearProgress />
                ) : (
                  recount && (
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      {recount.matched.toLocaleString()} éléments sélectionnés (
                      {recount.matchedProducts.toLocaleString()} produits)
                      {recount.matched > 50000 && ' — sera tronqué à 50 000'}
                    </Typography>
                  )
                )}
              </Box>
            </Box>

            <Divider />

            <TextField
              label="Nom du catalogue"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
              placeholder="Électroménager"
            />
            <TextField
              label="Description (optionnel)"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Ex. Gros électroménager cuisine — lave-linge, réfrigérateurs, fours. Réutilisée comme contexte lors de la création de contenu et de la segmentation."
              helperText={`${description.length}/500`}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Icône du catalogue
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {CATALOGUE_ICON_KEYS.map((k) => (
                  <IconButton
                    key={k}
                    size="small"
                    onClick={() => setIcon(k)}
                    sx={{
                      border: 1,
                      borderColor: icon === k ? 'primary.main' : 'divider',
                      bgcolor: icon === k ? 'action.selected' : 'transparent',
                      color: icon === k ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    <CatalogueIcon icon={k} fontSize="small" />
                  </IconButton>
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Langue du catalogue
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                {LANGUAGES.map((l) => (
                  <Chip
                    key={l.code}
                    label={`${l.flag} ${l.label}`}
                    color={language === l.code ? 'primary' : 'default'}
                    variant={language === l.code ? 'filled' : 'outlined'}
                    onClick={() => setLanguage(l.code)}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>

            {preview && preview.sampleItems.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Aperçu (20 premiers) :
                </Typography>
                <Box sx={{ maxHeight: 160, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.5 }}>
                  <Table size="small">
                    <TableBody>
                      {preview.sampleItems.map((it) => (
                        <TableRow key={it.url}>
                          <TableCell sx={{ py: 0.25 }}>
                            <Typography variant="caption">{it.name || shortName(it.url)}</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <Chip size="small" label={it.type} variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'preview' && (
          <Button onClick={() => setStep('source')} disabled={creating}>
            Retour
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={creating || parsing}>
          Annuler
        </Button>
        {step === 'source' ? (
          <Button variant="contained" onClick={() => doPreview()} disabled={!canPreview || parsing}>
            {parsing ? <CircularProgress size={20} /> : 'Analyser'}
          </Button>
        ) : (
          <Button variant="contained" onClick={doCreate} disabled={creating || !name.trim()}>
            {creating ? <CircularProgress size={20} /> : 'Créer le catalogue'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <Dialog open={wafHelpOpen} onClose={() => setWafHelpOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Autoriser le robot Traaker</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Le pare-feu du site (Cloudflare, Akamai…) bloque notre détection de sitemap.
          Traaker ne contourne pas ces protections — faites autoriser notre robot par
          votre équipe technique, puis relancez la détection. En attendant, vous pouvez
          importer un CSV.
        </Alert>
        <WafWhitelistInstructions />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setWafHelpOpen(false)}>Fermer</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
